import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import {
  SALES_DISPLAY_PREVIEW_PARAM,
  SALES_DISPLAY_PREVIEW_VALUE,
  SALES_SCREEN_PATH,
  formatLoopDuration,
  isSalesDisplayPreviewState,
  salesDisplayLoopSeconds,
  type SalesDisplayPreviewCommand,
  type SalesDisplayPreviewScreen,
} from "@/lib/sales-display-preview";
import { ScaledDisplayFrame } from "./ScaledDisplayFrame";

// Peker på skjermruta, ikke /display/salg: den siste ligger inne i AppLayout,
// så previewen ville vist appens sidemeny rundt skjermbildet i stedet for det
// skjermen faktisk viser.
const PREVIEW_SRC = `${SALES_SCREEN_PATH}?${SALES_DISPLAY_PREVIEW_PARAM}=${SALES_DISPLAY_PREVIEW_VALUE}`;

/**
 * VIB-2705: hele salgsdisplay-loopen slik TV-en kjører den, med bla fram og
 * tilbake.
 *
 * Skjermlista kommer FRA displayet, ikke herfra. Det er med vilje: én
 * widget-rad i oppsettet blir flere skjermer på TV-en (Hall of Fame blir tre,
 * «Salgspuls + alle selgere» blir én per salgsenhet), og den ekspansjonen bor i
 * `configuredSalesDisplayLoop`. Regnet vi den ut på nytt her, ville vi hatt to
 * steder å holde i takt, og previewen kunne vist en annen loop enn skjermen.
 */
export function SalesDisplayLoopPreview() {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const [screens, setScreens] = useState<SalesDisplayPreviewScreen[]>([]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!isSalesDisplayPreviewState(event.data)) return;
      setScreens(event.data.screens);
      setIndex(event.data.index);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const send = useCallback((command: SalesDisplayPreviewCommand) => {
    frameRef.current?.contentWindow?.postMessage(command, window.location.origin);
  }, []);

  const total = screens.length;
  const current = screens[index];
  const loopSeconds = salesDisplayLoopSeconds(screens);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h3 className="font-medium">Slik ser loopen ut på skjermen</h3>
          <p className="text-sm text-muted-foreground">
            {total === 0
              ? "Laster loopen …"
              : `Skjerm ${index + 1} av ${total} · hele loopen tar ${formatLoopDuration(loopSeconds)}`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            aria-label="Forrige skjerm"
            disabled={total === 0}
            onClick={() => send({ type: "sales-display-preview/command", action: "prev" })}
          >
            <SrIcon name="chevron-left" className="size-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            aria-label="Neste skjerm"
            disabled={total === 0}
            onClick={() => send({ type: "sales-display-preview/command", action: "next" })}
          >
            <SrIcon name="chevron-right" className="size-4" />
          </Button>
        </div>
      </div>

      <ScaledDisplayFrame
        src={PREVIEW_SRC}
        title="Forhåndsvisning av salgsdisplayet"
        onReady={(frame) => {
          frameRef.current = frame;
        }}
      />

      {current && (
        <p className="text-sm">
          <span className="font-medium">{current.label}</span>
          <span className="text-muted-foreground"> · {current.durationSeconds} sek</span>
        </p>
      )}

      {total > 1 && (
        <div className="flex flex-wrap gap-1" role="list" aria-label="Skjermer i loopen">
          {screens.map((screen, position) => (
            <button
              key={`${screen.id}-${position}`}
              type="button"
              role="listitem"
              onClick={() =>
                send({ type: "sales-display-preview/command", action: "goto", index: position })
              }
              className={`rounded-md border px-2 py-1 text-xs ${
                position === index
                  ? "border-primary bg-primary/10 font-medium text-primary"
                  : "text-muted-foreground hover:bg-muted"
              }`}
            >
              {position + 1}. {screen.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
